// @ts-strict-ignore
import { Button, Flex } from '@backend/lib-design-system';
import { LocationFilledIcon, PreferenceFilledIcon, WorkFilledIcon } from '@backend/lib-design-system/icons';
import { convertDistance, DistanceUnit } from '@backend/lib-l10n-logic';
import { type FC, useState } from 'react';

import { AddressSelect } from '~/components/AddressSelect';
import { DistanceSlider } from '~/components/DistanceSlider';
import { FilterField } from '~/components/FiltersPanel/FilterField';
import { FiltersHeaderInfo } from '~/components/FiltersPanel/FiltersHeaderInfo';
import { FiltersPanelSelect } from '~/components/FiltersPanel/FiltersPanelSelect';
import { JobSelect } from '~/components/JobSelect';
import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import { useSearchFormContext } from '~/contexts/SearchFormContext/SearchFormContext';
import { useEffectIfMounted, useIsMobileOrTablet, useTranslation } from '~/hooks';
import { getAddressDisplayName } from '~/utils/address';
import { getDistanceConfig } from '~/utils/search/getDistanceConfig';

import {
  CustomSearchButton,
  FiltersAddressAndJobFieldsContainer,
  FiltersAndSearchButtonContainer,
  FiltersContainer,
  FiltersHeaderContainer,
  FiltersPanelContainer,
  HeaderTitle,
} from './FiltersPanel.styles';

interface FiltersPanelProps {
  showFiltersPanelForm: boolean;
  onToggleFiltersPanelForm: () => void;
}

export const FiltersPanel: FC<FiltersPanelProps> = ({ showFiltersPanelForm, onToggleFiltersPanelForm }) => {
  const { country, distanceUnit, iframe } = useBusinessUnitContext();
  const t = useTranslation();
  const isMobileOrTablet = useIsMobileOrTablet();

  const {
    form: {
      sort: { value: sort },
      type: { value: type },
      distance: { value: distance },
      job: { value: job, defaultValue: defaultJob },
      experience: { value: experience },
      address: { value: address, defaultValue: defaultAddress },
    },
    submit,
    resetFields,
  } = useSearchFormContext();

  const distanceConfig = getDistanceConfig(country);
  const [currentDistance, setCurrentDistance] = useState(distance);

  useEffectIfMounted(async () => {
    if (!isMobileOrTablet) {
      await submit();
    }
  }, [sort, type, experience, distance, job || undefined, address?.location?.latitude, address?.location?.longitude]);

  const handleToggleFiltersPanelForm = () => {
    onToggleFiltersPanelForm();
    resetFields();
  };

  return (
    <FiltersPanelContainer>
      <FiltersHeaderContainer space="xs">
        <Flex.Row space="xs" justifyContent="space-between" alignItems="flex-end">
          {defaultAddress || defaultJob ? <HeaderTitle>{t('form_title')}</HeaderTitle> : <div></div>}
          <Button variant="transparent" size="s" onClick={handleToggleFiltersPanelForm}>
            <PreferenceFilledIcon size="xl" />
          </Button>
        </Flex.Row>
        {(defaultAddress || defaultJob) && (
          <Flex.Row space="l">
            {defaultAddress && (
              <FiltersHeaderInfo Icon={LocationFilledIcon} text={getAddressDisplayName(defaultAddress)} />
            )}
            {defaultJob && <FiltersHeaderInfo Icon={WorkFilledIcon} text={defaultJob} />}
          </Flex.Row>
        )}
      </FiltersHeaderContainer>
      <FiltersAndSearchButtonContainer showFiltersPanelForm={showFiltersPanelForm}>
        <FiltersContainer>
          <FiltersAddressAndJobFieldsContainer $isIframe={iframe}>
            <FilterField type="address">
              <AddressSelect />
            </FilterField>
            <FilterField type="job">
              <JobSelect />
            </FilterField>
          </FiltersAddressAndJobFieldsContainer>
          <FilterField type="sort">
            <FiltersPanelSelect type="sort" />
          </FilterField>
          <FilterField type="type">
            <FiltersPanelSelect type="type" hasDefaultOptionAll />
          </FilterField>
          <FilterField
            type="distance"
            labelData={{
              distance: convertDistance({ value: currentDistance, unit: DistanceUnit.KM }, distanceUnit, {
                precision: 0,
              }).value,
            }}
          >
            <DistanceSlider
              min={distanceConfig.minDistance}
              max={distanceConfig.maxDistance}
              onCurrentDistanceChange={setCurrentDistance}
            />
          </FilterField>
          <FilterField type="experience">
            <FiltersPanelSelect type="experience" hasDefaultOptionAll />
          </FilterField>
        </FiltersContainer>
        <CustomSearchButton />
      </FiltersAndSearchButtonContainer>
    </FiltersPanelContainer>
  );
};
